import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Stack,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DataGrid } from "@mui/x-data-grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Page, Text, View, Document, PDFViewer } from "@react-pdf/renderer";
import { useMaterialUIController } from "context";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  FP,
  PageHeader,
  StatCard,
  fpDataGridSx,
  fpInputSx,
} from "components/financepro";
import { useApi, useSucursales } from "lib/api";

function Informepagos() {
  const [fecha1, setFecha1] = useState("");
  const [fecha2, setFecha2] = useState("");
  const [nosucursal, setNosucursal] = useState("");
  const [rows, setRows] = useState([]);
  const [presentarpdf, setPresentarpdf] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pagoTarget, setPagoTarget] = useState(null);
  const [filtroNombre, setFiltroNombre] = useState("");

  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { post } = useApi();
  const sucursal = useSucursales();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const columns = useMemo(
    () => [
      { field: "noprest", headerName: "DOC #", width: 100, align: "center", headerAlign: "center" },
      { field: "fecha", headerName: "Fecha", width: 110, align: "center", headerAlign: "center" },
      { field: "nombres", headerName: "Nombres", flex: 1, minWidth: 160 },
      { field: "valor", headerName: "Valor", width: 130 },
      {
        field: "vpagcap",
        headerName: "Capital",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
        
      },
      {
        field: "vpagint",
        headerName: "Interés",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "vpagmora",
        headerName: "Mora",
        type: "number",
        width: 120,
        align: "right",
        headerAlign: "right",
      },
      {
        field: "acciones",
        headerName: "Acciones",
        width: 100,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton size="small" color="error" onClick={() => abrirConfirmacion(params.row)}>
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    []
  );

  async function procesarrequerimiento() {
    const { status, data } = await post("/api/informespagos", {
      fechadesde: fecha1,
      fechahasta: fecha2,
      sucursal: nosucursal,
    });
    if (status === 200) {
      const rawRows = Array.isArray(data?.prestamo)
        ? data.prestamo
        : Array.isArray(data?.prestamos)
        ? data.prestamos
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];
      const normalizedRows = rawRows.map((item, index) => ({
        ...item,
        id: item?.id ?? item?.doc ?? item?.documento ?? item?.prestamo ?? `row-${index}`,
      }));
      setRows(normalizedRows);
      setFiltroNombre("");
    }
    if (status === 400) {
      setRows([]);
      alert(data["Error"]);
    }
  }

  function abrirConfirmacion(row) {
    setPagoTarget(row);
    setConfirmOpen(true);
  }

  async function cancelarPago() {
    if (!pagoTarget) return;
    try {
      const { status, data } = await post("/api/cancelarpagos", {
        norecibo: pagoTarget.norecibo || pagoTarget.id,
        noprest: pagoTarget.noprest,
        sucursal: pagoTarget.sucursal || "",
        tipodeusuario: "",
      });
      if (status === 200) {
        setRows((prev) =>
          prev.filter(
            (r) =>
              !(
                r.id === pagoTarget.id ||
                (r.norecibo === pagoTarget.norecibo && r.noprest === pagoTarget.noprest)
              )
          )
        );
        setConfirmOpen(false);
        setPagoTarget(null);
        alert("Pago cancelado");
      } else {
        alert(data?.Error || "No se pudo cancelar el pago");
        setConfirmOpen(false);
      }
    } catch (err) {
      alert("Error de red: " + err.message);
      setConfirmOpen(false);
    }
  }

  const rowsFiltradas = useMemo(() => {
    const q = filtroNombre.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const n = String(r.nombres ?? "").toLowerCase();
      const a = String(r.apellidos ?? "").toLowerCase();
      return n.includes(q) || a.includes(q);
    });
  }, [rows, filtroNombre]);

  const fmtMoney = (v) =>
    Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const totalCapital = rowsFiltradas.reduce(
    (acc, r) => acc + parseFloat(String(r.vpagcap || "0").replace(/,/g, "")),
    0
  );
  const totalInteres = rowsFiltradas.reduce(
    (acc, r) => acc + parseFloat(String(r.vpagint || "0").replace(/,/g, "")),
    0
  );
  const totalMora = rowsFiltradas.reduce(
    (acc, r) => acc + parseFloat(String(r.vpagmora || "0").replace(/,/g, "")),
    0
  );

  function handlepdf() {
    if (rowsFiltradas.length === 0) {
      alert("No hay datos para procesar este reporte");
    } else {
      setPresentarpdf(true);
    }
  }

  const MyDocument = ({ rowsData, nombreNegocio1 }) => {
    const tCap = (rowsData || []).reduce(
      (s, r) => s + (parseFloat(String(r.vpagcap || 0).replace(/,/g, "").trim()) || 0),
      0
    );
    const tInt = (rowsData || []).reduce(
      (s, r) => s + (parseFloat(String(r.vpagint || 0).replace(/,/g, "").trim()) || 0),
      0
    );
    const tMora = (rowsData || []).reduce(
      (s, r) => s + (parseFloat(String(r.vpagmora || 0).replace(/,/g, "").trim()) || 0),
      0
    );

    return (
      <PDFViewer style={{ height: isXs ? "calc(100vh - 120px)" : "550px", width: "100%" }}>
        <Document>
          <Page size="A4" orientation="Portrait" wrap style={{ paddingBottom: 65 }}>
            <View style={{ textAlign: "center", width: "94%", fontSize: 10, textTransform: "uppercase", marginTop: "15px" }} fixed>
              <Text>{nombreNegocio1 || "Empresa"}</Text>
            </View>
            <View style={{ textAlign: "center", width: "94%", fontSize: 9, textTransform: "uppercase" }} fixed>
              <Text>Informe de Pagos Realizados</Text>
            </View>
            <View style={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "left", width: "94%", height: "25px", fontSize: 10, backgroundColor: "#1976d2", color: "white", marginLeft: "15px", marginTop: "10px" }} fixed>
              <Text style={{ width: "60px", textAlign: "left", fontWeight: "bold" }}>Doc#</Text>
              <Text style={{ width: "70px", textAlign: "left", fontWeight: "bold" }}>Fecha</Text>
              <Text style={{ width: "170px", textAlign: "left", fontWeight: "bold" }}>Nombres</Text>
              <Text style={{ width: "75px", textAlign: "right", fontWeight: "bold" }}>Cantidad</Text>
              <Text style={{ width: "75px", textAlign: "right", fontWeight: "bold" }}>Capital</Text>
              <Text style={{ width: "75px", textAlign: "right", fontWeight: "bold" }}>Interés</Text>
              <Text style={{ width: "75px", textAlign: "right", fontWeight: "bold" }}>Mora</Text>
            </View>
            {(rowsData || []).map((r, i) => (
              <View key={i} style={{ flexDirection: "row", width: "94%", height: "15px", fontSize: 9, color: "black", marginLeft: "15px", backgroundColor: i % 2 === 0 ? "#f9f9f9" : "white" }}>
                <Text style={{ width: "60px", textAlign: "left" }}>{r.noprest ?? r.id}</Text>
                <Text style={{ width: "70px", textAlign: "left" }}>{r.fecha}</Text>
                <Text style={{ width: "170px", textAlign: "left" }}>{r.nombres}</Text>
                <Text style={{ width: "75px", textAlign: "right" }}>{r.cantsol}</Text>
                <Text style={{ width: "75px", textAlign: "right" }}>{fmtMoney(r.vpagcap)}</Text>
                <Text style={{ width: "75px", textAlign: "right" }}>{fmtMoney(r.vpagint)}</Text>
                <Text style={{ width: "75px", textAlign: "right" }}>{fmtMoney(r.vpagmora)}</Text>
              </View>
            ))}
            <Text> </Text>
            <View style={{ flexDirection: "row", width: "94%", height: "25px", fontSize: 10, backgroundColor: "#f5f5f5", color: "black", marginLeft: "15px", fontWeight: "bold", borderTop: "2px solid #1976d2" }}>
              <Text style={{ width: "60px", textAlign: "left" }}>Totales</Text>
              <Text style={{ width: "70px" }}> </Text>
              <Text style={{ width: "170px" }}> </Text>
              <Text style={{ width: "75px" }}> </Text>
              <Text style={{ width: "75px", textAlign: "right" }}>{fmtMoney(tCap)}</Text>
              <Text style={{ width: "75px", textAlign: "right" }}>{fmtMoney(tInt)}</Text>
              <Text style={{ width: "75px", textAlign: "right" }}>{fmtMoney(tMora)}</Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <PageHeader
          title="Pagos"
          subtitle="Informe de Pagos Realizados"
          cta={
            <Button
              variant="contained"
              startIcon={<FileDownloadRoundedIcon />}
              disabled={rowsFiltradas.length === 0}
              onClick={handlepdf}
              sx={{
                bgcolor: FP.color.navy,
                color: "#FFFFFF",
                borderRadius: 1.4,
                px: 2.2,
                height: 38,
                boxShadow: "none",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#E2E8F0", color: "#94A3B8" },
              }}
            >
              Export Data
            </Button>
          }
        />

        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: "#FAFBFE",
                display: "flex",
                alignItems: "center",
                gap: 1.2,
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FilterAltRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
                Filtros de Búsqueda
              </Typography>
              <Typography sx={{ ml: "auto", color: FP.color.textSecondary, fontSize: "0.78rem", fontWeight: 600 }}>
                Rango · Sucursal
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Fecha Desde"
                    value={fecha1}
                    onChange={(e) => setFecha1(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Fecha Hasta"
                    value={fecha2}
                    onChange={(e) => setFecha2(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <Autocomplete
                    disablePortal
                    options={sucursal || []}
                    value={nosucursal || null}
                    onChange={(event, value) => setNosucursal(value?.label || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sucursal"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        sx={fpInputSx}
                      />
                    )}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={() => procesarrequerimiento()}
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    height: 46,
                    bgcolor: FP.color.navy,
                    color: "#FFFFFF",
                    borderRadius: 1.4,
                    textTransform: "none",
                    fontSize: "0.94rem",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: FP.color.navyDeep, boxShadow: "none" },
                  }}
                >
                  Procesar
                </Button>
              </Box>
            </CardContent>
          </Card>

          {rows.length > 0 && (
            <Box
              sx={{
                display: "grid",
                width: "100%",
                gap: 2,
                mb: 3,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                },
              }}
            >
              <StatCard
                label="Total registros"
                value={String(rowsFiltradas.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
              <StatCard
                label="Total Capital"
                value={`$${fmtMoney(totalCapital)}`}
                icon={AccountBalanceWalletOutlinedIcon}
                accent={FP.color.navy}
              />
              <StatCard
                label="Total Interés"
                value={`$${fmtMoney(totalInteres)}`}
                icon={TrendingUpOutlinedIcon}
                accent={FP.color.accentBlue}
              />
              <StatCard
                label="Total Mora"
                value={`$${fmtMoney(totalMora)}`}
                icon={WarningAmberOutlinedIcon}
                accent={FP.color.danger}
              />
            </Box>
          )}

          <Card
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.cardBg,
              boxShadow: FP.shadow.soft,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: "#FAFBFE",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: "0.98rem" }}>
                Listado de pagos realizados
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flexWrap: "wrap" }}>
                {rows.length > 0 && (
                  <TextField
                    size="small"
                    placeholder="Buscar por nombre o apellido"
                    value={filtroNombre}
                    onChange={(e) => setFiltroNombre(e.target.value)}
                    sx={{
                      minWidth: { xs: "100%", sm: 260 },
                      "& .MuiOutlinedInput-root": {
                        height: 36,
                        borderRadius: 1.2,
                        bgcolor: "#FFFFFF",
                        fontSize: "0.86rem",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: FP.color.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                {rowsFiltradas.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => handlepdf()}
                    sx={{
                      borderColor: FP.color.danger,
                      color: FP.color.danger,
                      borderRadius: 1.2,
                      textTransform: "none",
                      fontWeight: 700,
                      "&:hover": { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                    }}
                  >
                    Generar PDF
                  </Button>
                )}
              </Box>
            </Box>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
              {rows.length > 0 ? (
                <>
                  <Box sx={{ display: { xs: "none", md: "block" }, width: "100%", overflowX: "auto" }}>
                    <DataGrid
                      rows={rowsFiltradas}
                      columns={columns}
                      getRowId={(row) => (row.id != null ? String(row.id) : `${row.norecibo ?? ""}-${row.noprest ?? ""}`)}
                      pageSizeOptions={[5, 10, 25, 50]}
                      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      checkboxSelection
                      disableRowSelectionOnClick
                      rowHeight={42}
                      sx={{ minWidth: { xs: 720, sm: "100%" }, height: 520, ...fpDataGridSx }}
                      autoHeight={false}
                    />
                  </Box>
                  <Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: 1.4 }}>
                    {rowsFiltradas.map((row, idx) => (
                      <Box
                        key={row.id != null ? String(row.id) : `card-${idx}`}
                        sx={{
                          border: `1px solid ${FP.color.border}`,
                          borderRadius: 2,
                          p: 1.5,
                          bgcolor: "#FFFFFF",
                          boxShadow: FP.shadow.soft,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: "1rem", lineHeight: 1.2 }}>
                              Doc #{row.noprest ?? row.id}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.85rem", fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}>
                              {row.nombres || "—"}
                            </Typography>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.75rem", mt: 0.1 }}>
                              {row.fecha || "—"}
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                              Cantidad
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: "1rem", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>
                              {row.cantsol ?? "—"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1 }}>
                          {[
                            ["Capital", fmtMoney(row.vpagcap)],
                            ["Interés", fmtMoney(row.vpagint)],
                            ["Mora", fmtMoney(row.vpagmora)],
                          ].map(([label, value]) => (
                            <Box key={label}>
                              <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                                {label}
                              </Typography>
                              <Typography sx={{ color: FP.color.textPrimary, fontSize: "0.92rem", fontWeight: 600, wordBreak: "break-word" }}>
                                ${value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 1.2, display: "flex", justifyContent: "flex-end" }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                            onClick={() => abrirConfirmacion(row)}
                            sx={{ borderRadius: 1.2, textTransform: "none", fontWeight: 700 }}
                          >
                            Cancelar pago
                          </Button>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    No hay datos para mostrar
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: "0.9rem" }} textAlign="center" px={2}>
                    Seleccione los filtros y haga clic en &quot;Procesar&quot; para obtener resultados.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {presentarpdf && (
            <Dialog
              open={presentarpdf}
              maxWidth="lg"
              fullWidth
              fullScreen={isXs}
              onClose={() => setPresentarpdf(false)}
              PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
            >
              <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" fontWeight="bold">
                  Vista Previa del Reporte PDF
                </Typography>
                <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: "auto", p: 1 }}>
                  <CancelIcon />
                </Button>
              </DialogTitle>
              <DialogContent>
                <MyDocument rowsData={rowsFiltradas} nombreNegocio1={nombreNegocio} />
              </DialogContent>
            </Dialog>
          )}

          <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Cancelar pago</DialogTitle>
            <DialogContent>
              <DialogContentText>
                ¿Seguro que deseas cancelar este pago? Recibo #{pagoTarget?.norecibo || pagoTarget?.id || ""}.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmOpen(false)}>No</Button>
              <Button color="error" variant="contained" onClick={cancelarPago}>
                Sí, cancelar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
    </Box>
  );
}

export default Informepagos;
